import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import ModalWrapper from '../../../Components/ModalWrapper/ModalWrapper';
import commonStyle from '../../../assets/common/CommonStyle.module.css';
import s from './ReceiveConfirmationPopup.module.css';
import CommonSvgSelector from '../../../assets/common/SVG/CommonSvgSelector';
import VeryLightGreenButton from '../../../Components/Buttons/VeryLightGreenButton';
import GreenButton from '../../../Components/Buttons/GreenButton';
import LightGreenButton from '../../../Components/Buttons/LightGreenButton';
import PreLoaderComponent from '../../../assets/Animations/PreLoaderComponent/PreLoaderComponent';
import ApproveAskPopup from '../../../Components/ApproveAskPopup/ApproveAskPopup';
import {
	useLazyGetReceiveListQuery,
	useApproveOrderMutation,
} from '../../../redux/DepotSlice/depotOrdersApi';
import {ProductTypeForApprove} from '../../../types/TypesStore';

type Props = {
	orderId: number;
	onClose: () => void;
	onSuccess: () => void;
};

const ReceiveConfirmationPopup: React.FC<Props> = ({
	orderId,
	onClose,
	onSuccess,
}) => {
	const {t} = useTranslation();
	const [trigger, {data, isLoading, error}] = useLazyGetReceiveListQuery();
	const [approveOrder] = useApproveOrderMutation();
	const [products, setProducts] = useState<ProductTypeForApprove[]>([]);
	const [productNames, setProductNames] = useState<{[key: number]: string}>({});
	const [productImages, setProductImages] = useState<{
		[key: number]: string | null;
	}>({});
	const [showConfirm, setShowConfirm] = useState(false);
	const [confirmAction, setConfirmAction] = useState<'approve' | 'error'>(
		'approve',
	);
	const [isSubmitting, setIsSubmitting] = useState(false);

	React.useEffect(() => {
		trigger(orderId);
	}, [orderId, trigger]);

	React.useEffect(() => {
		if (data?.order_products) {
			// Группируем товары по product_id и суммируем количество
			const groupedProducts = data.order_products.reduce(
				(acc: any, prod: any) => {
					const productId = prod.product_id;
					if (acc[productId]) {
						acc[productId].count += prod.count;
					} else {
						acc[productId] = {
							product_id: productId,
							name: prod.name,
							img: prod.img,
							count: prod.count,
						};
					}
					return acc;
				},
				{},
			);

			// Преобразуем в массив для компонента
			const initialProducts: ProductTypeForApprove[] = Object.values(
				groupedProducts,
			).map((prod: any) => ({
				product_id: prod.product_id,
				count_shipped: +prod.count, // Замовлено - берем из API
				count_receive: 0, // Отримано - пользователь вводит
				count_div: +prod.count, // Різниця - изначально равна замовлено
			}));

			// Сохраняем названия товаров и изображения
			const names: {[key: number]: string} = {};
			const images: {[key: number]: string | null} = {};
			Object.values(groupedProducts).forEach((prod: any) => {
				names[prod.product_id] = prod.name;
				images[prod.product_id] = prod.img;
			});

			setProducts(initialProducts);
			setProductNames(names);
			setProductImages(images);
		}
	}, [data]);

	const handleProductChange = (productId: number, value: number) => {
		setProducts((prev) =>
			prev.map((prod) => {
				if (prod.product_id === productId) {
					const newCountReceive = value;
					const newCountDiv = prod.count_shipped - newCountReceive;
					return {
						...prod,
						count_receive: newCountReceive,
						count_div: newCountDiv,
					};
				}
				return prod;
			}),
		);
	};

	const handleMarkAll = () => {
		setProducts((prev) =>
			prev.map((prod) => ({
				...prod,
				count_receive: prod.count_shipped,
				count_div: 0,
			})),
		);
	};

	const hasNonZeroDifferences = products.some((prod) => prod.count_div !== 0);
	const hasAllZeroDifferences = products.every((prod) => prod.count_div === 0);

	const handleConfirmApprove = () => {
		setConfirmAction('approve');
		setShowConfirm(true);
	};

	const handleConfirmReturn = () => {
		setConfirmAction('error');
		setShowConfirm(true);
	};

	const handleFinalConfirm = async () => {
		setIsSubmitting(true);

		try {
			const result = await approveOrder({
				order_id: orderId,
				action: confirmAction,
				comment: '',
				products,
			}).unwrap();

			toast.success(t('Замовлення підтверджено'));
			onSuccess();
			onClose();
		} catch (error) {
			const errorMessage =
				(error as any)?.data?.message?.textmsg || t('Щось пішло не так');
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
			setShowConfirm(false);
		}
	};

	return (
		<>
			<ModalWrapper handleClosePopup={showConfirm ? () => {} : onClose}>
				<div className={commonStyle.popupWrapper}>
					<div className={s.container}>
						<div className={s.closeCross}>
							<button type={'button'} onClick={onClose}>
								<CommonSvgSelector id={'closeCross'} />
							</button>
						</div>
						<div className={s.title}>
							<h4>
								{t('Накладна №')} {orderId}
							</h4>
						</div>

						{/* Блок с пояснением */}
						<div className={s.explanationBlock}>
							<p>
								{t(
									'Перевірте вашу посилку, порахуйте всі товари та введіть в колонку "Отримано" кількість отриманого товару. Далі "Підтвердіть отримання" або "поверніть недостачу". Натискаючи на ці кнопки ви погоджуєтесь з тим що отримали ці товари і вони додадуться до балансу вашого складу.',
								)}
							</p>
						</div>

						<div className={s.content}>
							{isLoading ? (
								<PreLoaderComponent type={'circle'} size={'big'} />
							) : !data ? (
								<div className={s.errorMessage}>
									<p>{t('Помилка завантаження даних')}</p>
								</div>
							) : !data.order_products || data.order_products.length === 0 ? (
								<div className={s.errorMessage}>
									<p>{t('Немає товарів для підтвердження')}</p>
								</div>
							) : (
								<div className={s.tableWrapper}>
									<div className={s.tableHeader}>
										<div className={s.headerCell}>{t('Товар')}</div>
										<div className={s.headerCell}>{t('Замовлено')}</div>
										<div className={s.headerCell}>{t('Отримано')}</div>
										<div className={s.headerCell}>{t('Різниця')}</div>
									</div>
									{products.map((product) => (
										<div key={product.product_id} className={s.tableRow}>
											<div className={s.cell}>
												<div className={s.productInfo}>
													{productImages[product.product_id] && (
														<img
															src={productImages[product.product_id]!}
															alt={productNames[product.product_id]}
															className={s.productImage}
														/>
													)}
													<span className={s.productName}>
														{productNames[product.product_id]}
													</span>
												</div>
											</div>
											<div className={s.cell}>
												<span className={s.readonlyValue}>
													{product.count_shipped}
												</span>
											</div>
											<div className={s.cell}>
												<input
													type="number"
													min="0"
													max={product.count_shipped}
													value={product.count_receive}
													onChange={(e) =>
														handleProductChange(
															product.product_id,
															+e.target.value,
														)
													}
													className={s.input}
												/>
											</div>
											<div className={s.cell}>
												<span
													className={`${s.differenceValue} ${product.count_div !== 0 ? s.nonZero : ''}`}
												>
													{product.count_div}
												</span>
											</div>
										</div>
									))}
								</div>
							)}
						</div>
						<div className={s.buttonsWrapper}>
							<LightGreenButton
								type={'button'}
								text={t('Відмітити всі')}
								onClick={handleMarkAll}
								disabled={isLoading || isSubmitting}
							/>
							<div className={s.actionButtons}>
								<GreenButton
									text={t('Підтвердити отримання')}
									onClick={handleConfirmApprove}
									disabled={isLoading || isSubmitting || !hasAllZeroDifferences}
								/>
								<VeryLightGreenButton
									text={t('Підтвердити та повернути недостачу')}
									onClick={handleConfirmReturn}
									disabled={isLoading || isSubmitting || !hasNonZeroDifferences}
								/>
							</div>
						</div>
					</div>
				</div>
			</ModalWrapper>

			{showConfirm && (
				<ApproveAskPopup
					approvedCallback={() => {
						handleFinalConfirm();
					}}
					canceledCallback={() => {
						setShowConfirm(false);
					}}
					questionText={
						confirmAction === 'approve'
							? t('Ви дійсно підтверджуєте отримання всіх товарів?')
							: t('Ви дійсно підтверджуєте отримання з поверненням недостачі?')
					}
				/>
			)}
		</>
	);
};

export default ReceiveConfirmationPopup;
